/**
 * Marks a persistence command that the adapter has proved did not commit.
 *
 * Adapters must only construct this error after a transactional command was
 * rejected and a canonical resume/read under the same request key confirmed
 * that no committed result exists. Network failures, idempotency conflicts,
 * and failures after a successful commit must remain unmarked.
 */
export class ConfirmedMutationRejectedError extends Error {
  readonly confirmedMutationCommitState = 'rejected_without_commit' as const;
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super(
      typeof cause === 'object' && cause !== null &&
      'message' in cause && typeof cause.message === 'string' &&
      cause.message.trim().length > 0
        ? cause.message
        : 'confirmed_mutation_rejected_without_commit',
    );
    this.name = 'ConfirmedMutationRejectedError';
    this.cause = cause;
  }
}

export function isConfirmedMutationRejectedError(
  error: unknown,
): error is ConfirmedMutationRejectedError {
  return (
    error instanceof Error &&
    'confirmedMutationCommitState' in error &&
    error.confirmedMutationCommitState === 'rejected_without_commit' &&
    'cause' in error
  );
}

/**
 * Marks a durable atomic request whose committed resource was deliberately
 * deleted later. The old key is terminal, but this is not proof that a new
 * payload should be submitted automatically: callers must stop the current
 * submit and wait for a fresh explicit user action.
 */
export class AtomicResourceDeletedError extends Error {
  readonly atomicMutationTerminalState = 'resource_deleted' as const;
  override readonly cause: unknown;

  constructor(cause: unknown) {
    super(
      cause instanceof Error
        ? cause.message
        : 'atomic_mutation_resource_deleted',
    );
    this.name = 'AtomicResourceDeletedError';
    this.cause = cause;
  }
}

export function isAtomicResourceDeletedError(
  error: unknown,
): error is AtomicResourceDeletedError {
  return (
    error instanceof Error &&
    'atomicMutationTerminalState' in error &&
    error.atomicMutationTerminalState === 'resource_deleted'
  );
}
